/**
 * @fileoverview Middleware Redux para atualizar globalData após operações CRUD.
 * Monitora actions fulfilled e re-busca dados do servidor para o Redux.
 *
 * @module globalContextMiddleware
 * @since 2.0.0
 */

import globalContextService from '../../services/globalContextService.js';
import {
  setProducts,
  setCategories,
  setBrands,
  setPromotions,
} from '../slices/globalDataSlice.js';

/**
 * Actions que devem disparar atualização do contexto global.
 */
const CONTEXT_UPDATE_ACTIONS = [
  'products/fetchProducts/fulfilled',
  'products/updateCategory/fulfilled',
  'products/updateBrand/fulfilled',
  'promotion/createPromotion/fulfilled',
  'promotion/updatePromotion/fulfilled',
  'promotion/deletePromotion/fulfilled',
];
 
/**
 * Mapeia actions para tipo de contexto e action Redux correspondente.
 */
const ACTION_MAP = {
  'products/fetchProducts/fulfilled': {
    type: 'products',
    fetch: () => globalContextService.fetchAndStoreProducts(),
    action: setProducts,
  },
  'products/updateCategory/fulfilled': {
    type: 'categories',
    fetch: () => globalContextService.fetchAndStoreCategories(),
    action: setCategories,
  },
  'products/updateBrand/fulfilled': {
    type: 'brands',
    fetch: () => globalContextService.fetchAndStoreBrands(),
    action: setBrands,
  },
  'promotion/createPromotion/fulfilled': {
    type: 'promotions',
    fetch: () => globalContextService.fetchAndStorePromotions(),
    action: setPromotions,
  },
  'promotion/updatePromotion/fulfilled': {
    type: 'promotions',
    fetch: () => globalContextService.fetchAndStorePromotions(),
    action: setPromotions,
  },
  'promotion/deletePromotion/fulfilled': {
    type: 'promotions',
    fetch: () => globalContextService.fetchAndStorePromotions(),
    action: setPromotions,
  },
};

/**
 * Middleware para atualizar globalData quando dados são modificados via CRUD.
 * @param {Object} store - Store Redux
 * @returns {Function} Middleware function
 */
export const globalContextMiddleware = (store) => (next) => (action) => {
  const result = next(action);

  if (CONTEXT_UPDATE_ACTIONS.includes(action.type)) {
    const mapping = ACTION_MAP[action.type];
    if (mapping) {
      refreshAndDispatch(store.dispatch, mapping).catch((error) => {
        console.warn(`Erro no refresh de ${mapping.type}:`, error);
      });
    }
  }

  return result;
};

/**
 * Re-busca dados do servidor, persiste no localStorage e despacha para Redux.
 * @async
 * @param {Function} dispatch - Redux dispatch
 * @param {Object} mapping - Mapeamento com fetch e action
 */
async function refreshAndDispatch(dispatch, mapping) {
  try {
    await new Promise((resolve) => setTimeout(resolve, 500));
    const success = await mapping.fetch();
    if (success) {
      const data = globalContextService.getLocalData(mapping.type);
      if (data) {
        dispatch(mapping.action(data));
      }
      console.log(`🔄 globalData atualizado via CRUD: ${mapping.type}`);
    }
  } catch (error) {
    console.error(`Erro ao atualizar ${mapping.type} após CRUD:`, error);
  }
}

export default globalContextMiddleware;
